<template>
  <!-- Pass Status Display - Shows the current status of the pass with appropriate icon -->
  <div class="d-flex py-5 px-5 align-items-center">
    <!-- Status Label -->
    <div
      class="pe-3 fw-bolder text-white"
      :class="[isPassLongRunning || isInQueue ? 'text-black' : '']"
    >
      {{ $t("common.status") }}:
    </div>

    <!-- Status Indicator Block -->
    <div
      class="status-block d-flex align-items-center"
      :class="[isPassLongRunning ? 'longRunning' : currentStatus]"
      data-test-id="student-active-pass-status"
    >
      <!-- Queue icon - group -->
      <span v-if="currentStatus === 'queue'" class="me-1">
        <svg
          width="15"
          height="16"
          viewBox="0 0 15 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1.25012 14.25C1.25012 11.4886 3.4887 9.25 6.25012 9.25C9.01156 9.25 11.2501 11.4886 11.2501 14.25H10.0001C10.0001 12.1789 8.32118 10.5 6.25012 10.5C4.17905 10.5 2.50012 12.1789 2.50012 14.25H1.25012ZM6.25012 8.625C4.17825 8.625 2.50012 6.94688 2.50012 4.875C2.50012 2.80312 4.17825 1.125 6.25012 1.125C8.322 1.125 10.0001 2.80312 10.0001 4.875C10.0001 6.94688 8.322 8.625 6.25012 8.625ZM6.25012 7.375C7.63137 7.375 8.75012 6.25625 8.75012 4.875C8.75012 3.49375 7.63137 2.375 6.25012 2.375C4.86887 2.375 3.75012 3.49375 3.75012 4.875C3.75012 6.25625 4.86887 7.375 6.25012 7.375ZM11.4274 9.68925C13.1654 10.4726 14.3751 12.22 14.3751 14.25H13.1251C13.1251 12.7275 12.2178 11.4169 10.9144 10.8294L11.4274 9.68925ZM10.9977 2.63326C12.2466 3.14814 13.1251 4.37726 13.1251 5.8125C13.1251 7.60637 11.7527 9.07825 10.0001 9.236V7.97787C11.0606 7.82637 11.8751 6.915 11.8751 5.8125C11.8751 4.94959 11.3761 4.20377 10.6507 3.84772L10.9977 2.63326Z"
            fill="#384D61"
          />
        </svg>
      </span>

      <!-- Waiting icon - hourglass -->
      <span
        v-if="currentStatus === 'waiting'"
        class="flaticon-hour-glass-2-optimized me-1"
      >
        <span class="path1" />
        <span class="path2" />
        <span class="path3" />
        <span class="path4" />
        <span class="path5" />
        <span class="path6" />
        <span class="path7" />
        <span class="path8" />
        <span class="path9" />
        <span class="path10" />
      </span>

      <!-- Approved icon - check mark -->
      <i v-if="currentStatus === 'approved'" class="fi flaticon-check me-1" />

      <!-- Expired icon - finish line -->
      <span
        v-if="currentStatus === 'expired'"
        class="flaticon-finish-line-optimized me-1"
      >
        <span class="path1" />
        <span class="path2" />
        <span class="path3" />
        <span class="path4" />
        <span class="path5" />
        <span class="path6" />
        <span class="path7" />
        <span class="path8" />
        <span class="path9" />
        <span class="path10" />
      </span>

      <!-- Status Text -->
      {{ getStatusText }}
    </div>

    <!-- Status Time Display -->
    <div class="mx-5 text-white d-block">
      <div
        v-if="currentStatus === 'expired' && activePass"
        data-test-id="student-active-pass-status-time"
      >
        {{ getExpiredDate(activePass) }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PassStatusDisplay"
}
</script>

<script setup>
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import helpers from "@/helpers/index"

const { t } = useI18n()

const props = defineProps({
  activePass: {
    type: Object,
    default: () => ({})
  },
  currentStatus: {
    type: String,
    default: ""
  },
  isPassLongRunning: {
    type: Boolean,
    default: false
  },
  passStatuses: {
    type: Object,
    default: () => ({})
  }
})

// Determine if the pass is in the queue
const isInQueue = computed(() => props.currentStatus === "queue")

// Compute status text based on pass state
const getStatusText = computed(() => {
  const statusKey =
    props.currentStatus === "expired" && props.activePass?.canceled_at != null
      ? "canceled"
      : props.currentStatus

  return props.passStatuses[statusKey] || t("common.unknown")
})

// Format expired date for display
const getExpiredDate = (pass) => {
  if (!pass) return ""
  return (
    helpers.transformDate?.(
      pass?.canceled_at ||
        pass?.ended_at ||
        pass?.expired_at ||
        pass?.completed_at ||
        pass?.approved_at,
      "MM-DD-YYYY h:mm A"
    ) || ""
  )
}
</script>
